import React, { useState, useMemo, useEffect, useRef } from 'react';
import { Gantt, Task, ViewMode } from 'gantt-task-react';
import "gantt-task-react/dist/index.css";
import { DragDropContext, Droppable, Draggable } from 'react-beautiful-dnd';
import { 
  Paper, 
  Box, 
  Typography, 
  Button, 
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  CircularProgress,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  FormHelperText,
  Slider,
  Tooltip,
  Chip,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  ListItemSecondaryAction,
  ToggleButton,
  ToggleButtonGroup,
  useTheme
} from '@mui/material';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { LocalizationProvider, DatePicker } from '@mui/x-date-pickers';
import InputAdornment from '@mui/material/InputAdornment';
import AddIcon from '@mui/icons-material/Add';
import FlagIcon from '@mui/icons-material/Flag';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import CalendarViewMonthIcon from '@mui/icons-material/CalendarViewMonth';
import CalendarViewWeekIcon from '@mui/icons-material/CalendarViewWeek';
import CalendarViewDayIcon from '@mui/icons-material/CalendarViewDay';
import PlaylistAddIcon from '@mui/icons-material/PlaylistAdd';
import DeleteSweepIcon from '@mui/icons-material/DeleteSweep';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import SearchIcon from '@mui/icons-material/Search';
import { format, addMonths, startOfMonth, endOfMonth, eachMonthOfInterval } from 'date-fns';
import { alpha } from '@mui/material/styles';

const productionPhases = [
  {
    id: 'development',
    name: 'Development',
    baseColor: '#FF9800', // Orange
    description: 'Initial concept development, script writing, and planning',
  },
  {
    id: 'pre-production',
    name: 'Pre-Production',
    baseColor: '#2196F3', // Blue
    description: 'Planning, scheduling, casting, location scouting',
  },
  {
    id: 'production',
    name: 'Production',
    baseColor: '#4CAF50', // Green
    description: 'Principal photography and filming',
  },
  {
    id: 'post-production',
    name: 'Post-Production',
    baseColor: '#9C27B0', // Purple
    description: 'Editing, visual effects, sound design',
  },
  {
    id: 'distribution',
    name: 'Distribution',
    baseColor: '#F44336', // Red
    description: 'Marketing, distribution, and release',
  },
];

const productionTemplates = {
  shortFilm: {
    name: 'Short Film Production',
    phases: [
      {
        name: 'Development',
        tasks: [
          { title: 'Script Writing', duration: 14, type: 'task' },
          { title: 'Script Review', duration: 7, type: 'task' },
          { title: 'Script Approval', duration: 1, type: 'milestone' },
          { title: 'Budget Planning', duration: 7, type: 'task' },
          { title: 'Location Scouting', duration: 7, type: 'task' },
        ],
      },
      {
        name: 'Pre-Production',
        tasks: [
          { title: 'Casting', duration: 14, type: 'task' },
          { title: 'Crew Hiring', duration: 14, type: 'task' },
          { title: 'Equipment Rental', duration: 7, type: 'task' },
          { title: 'Production Schedule', duration: 7, type: 'task' },
          { title: 'Pre-Production Complete', duration: 1, type: 'milestone' },
        ],
      },
      {
        name: 'Production',
        tasks: [
          { title: 'Principal Photography', duration: 7, type: 'task' },
          { title: 'B-Roll Filming', duration: 3, type: 'task' },
          { title: 'Production Wrap', duration: 1, type: 'milestone' },
        ],
      },
      {
        name: 'Post-Production',
        tasks: [
          { title: 'Footage Review', duration: 7, type: 'task' },
          { title: 'Rough Cut', duration: 14, type: 'task' },
          { title: 'Fine Cut', duration: 7, type: 'task' },
          { title: 'Sound Design', duration: 7, type: 'task' },
          { title: 'Color Grading', duration: 7, type: 'task' },
          { title: 'Final Cut Approval', duration: 1, type: 'milestone' },
        ],
      },
      {
        name: 'Distribution',
        tasks: [
          { title: 'Festival Submission Prep', duration: 7, type: 'task' },
          { title: 'Marketing Materials', duration: 7, type: 'task' },
          { title: 'Film Release', duration: 1, type: 'milestone' },
        ],
      },
    ],
  },
  featureFilm: {
    name: 'Feature Film Production',
    phases: [
      {
        name: 'Development',
        tasks: [
          { title: 'Script Development', duration: 60, type: 'task' },
          { title: 'Script Reviews', duration: 30, type: 'task' },
          { title: 'Final Draft Approval', duration: 1, type: 'milestone' },
          { title: 'Budget Development', duration: 30, type: 'task' },
          { title: 'Funding Secured', duration: 1, type: 'milestone' },
        ],
      },
      {
        name: 'Pre-Production',
        tasks: [
          { title: 'Casting Director Hiring', duration: 14, type: 'task' },
          { title: 'Principal Cast Auditions', duration: 30, type: 'task' },
          { title: 'Supporting Cast Auditions', duration: 30, type: 'task' },
          { title: 'Location Scouting', duration: 30, type: 'task' },
          { title: 'Production Design', duration: 45, type: 'task' },
          { title: 'Crew Assembly', duration: 30, type: 'task' },
          { title: 'Pre-Production Complete', duration: 1, type: 'milestone' },
        ],
      },
      {
        name: 'Production',
        tasks: [
          { title: 'Principal Photography', duration: 45, type: 'task' },
          { title: 'Second Unit Filming', duration: 30, type: 'task' },
          { title: 'Production Wrap', duration: 1, type: 'milestone' },
        ],
      },
      {
        name: 'Post-Production',
        tasks: [
          { title: 'Assembly Cut', duration: 30, type: 'task' },
          { title: 'Rough Cut', duration: 45, type: 'task' },
          { title: 'Fine Cut', duration: 30, type: 'task' },
          { title: 'Picture Lock', duration: 1, type: 'milestone' },
          { title: 'Sound Design', duration: 30, type: 'task' },
          { title: 'Music Composition', duration: 45, type: 'task' },
          { title: 'VFX', duration: 60, type: 'task' },
          { title: 'Color Grading', duration: 30, type: 'task' },
          { title: 'Final Mix', duration: 14, type: 'task' },
          { title: 'Final Cut Approval', duration: 1, type: 'milestone' },
        ],
      },
      {
        name: 'Distribution',
        tasks: [
          { title: 'Marketing Plan', duration: 90, type: 'task' },
          { title: 'Festival Strategy', duration: 30, type: 'task' },
          { title: 'Distribution Strategy', duration: 45, type: 'task' },
          { title: 'Theatrical Release', duration: 1, type: 'milestone' },
        ],
      },
    ],
  },
  animation: {
    name: 'Animation Production',
    phases: [
      {
        name: 'Development',
        tasks: [
          { title: 'Story Development', duration: 45, type: 'task' },
          { title: 'Character Design', duration: 30, type: 'task' },
          { title: 'Style Guide', duration: 30, type: 'task' },
          { title: 'Concept Art', duration: 45, type: 'task' },
          { title: 'Story Approval', duration: 1, type: 'milestone' },
        ],
      },
      {
        name: 'Pre-Production',
        tasks: [
          { title: 'Storyboarding', duration: 60, type: 'task' },
          { title: 'Animatic', duration: 45, type: 'task' },
          { title: 'Asset Creation', duration: 90, type: 'task' },
          { title: 'Rigging', duration: 45, type: 'task' },
          { title: 'Pre-Production Complete', duration: 1, type: 'milestone' },
        ],
      },
      {
        name: 'Production',
        tasks: [
          { title: 'Layout', duration: 45, type: 'task' },
          { title: 'Animation', duration: 120, type: 'task' },
          { title: 'Lighting', duration: 45, type: 'task' },
          { title: 'FX Animation', duration: 60, type: 'task' },
          { title: 'Production Complete', duration: 1, type: 'milestone' },
        ],
      },
      {
        name: 'Post-Production',
        tasks: [
          { title: 'Rendering', duration: 60, type: 'task' },
          { title: 'Compositing', duration: 45, type: 'task' },
          { title: 'Sound Design', duration: 30, type: 'task' },
          { title: 'Music', duration: 45, type: 'task' },
          { title: 'Final Mix', duration: 14, type: 'task' },
          { title: 'Final Delivery', duration: 1, type: 'milestone' },
        ],
      },
    ],
  },
};

// Get phase color based on theme mode
const getPhaseColor = (baseColor, theme) => {
  // For dark mode, brighten some colors to ensure visibility
  if (theme.palette.mode === 'dark') {
    switch (baseColor) {
      case '#FF9800': // Orange - make brighter
        return '#FFB74D';
      case '#2196F3': // Blue - make brighter
        return '#64B5F6';
      case '#4CAF50': // Green - make brighter
        return '#81C784';
      case '#9C27B0': // Purple - make brighter
        return '#BA68C8';
      case '#F44336': // Red - make brighter
        return '#E57373';
      default:
        return baseColor;
    }
  }
  return baseColor;
};

// Custom styles for the Gantt chart
const getCustomStyles = (theme) => ({
  wrapper: {
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    padding: '20px',
    backgroundColor: theme.palette.background.paper,
    minHeight: 0,
    overflow: 'hidden'
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '20px',
    padding: '0 16px',
    backgroundColor: theme.palette.mode === 'dark' ? theme.palette.background.default : '#f5f5f5',
    borderBottom: `2px solid ${theme.palette.divider}`
  },
  '@global': {
    '.gantt-task-react__chart-timeline-grid': {
      backgroundColor: `${theme.palette.background.paper} !important`,
    },
    '.gantt-task-react__chart-timeline-grid-row': {
      backgroundColor: `${theme.palette.background.paper} !important`,
    },
    '.gantt-task-react__chart': {
      backgroundColor: `${theme.palette.background.paper} !important`,
    },
    '.gantt-task-react__chart-timeline': {
      backgroundColor: `${theme.palette.background.paper} !important`,
    }
  }
});

// Enhanced, modern tooltip component
const TaskTooltip = ({ task }) => {
  // Compute humanized duration in days (inclusive)
  let durationLabel = '';
  try {
    const start = new Date(task.start);
    const end = new Date(task.end);
    const msPerDay = 24 * 60 * 60 * 1000;
    const diff = Math.max(0, Math.ceil((end.setHours(0,0,0,0) - start.setHours(0,0,0,0)) / msPerDay)) + 1;
    durationLabel = isNaN(diff) ? '' : `${diff} ${diff === 1 ? 'day' : 'days'}`;
  } catch (_) {}

  return (
    <Paper 
      sx={{ 
        p: 1.5, 
        maxWidth: 320,
        borderRadius: 2,
        borderLeft: `4px solid ${task?.styles?.backgroundColor || '#1976d2'}`,
      }}
      elevation={3}
    >
      <Typography 
        variant="subtitle2" 
        noWrap 
        title={task.name}
        sx={{ mb: 0.5 }}
      >
        {task.name}
      </Typography>

      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mb: 1 }}>
        {task.project && (
          <Box 
            component="span"
            sx={{
              display: 'inline-block',
              px: 1,
              py: 0.25,
              borderRadius: 1,
              fontSize: 12,
              lineHeight: 1.4,
              bgcolor: 'action.selected',
            }}
          >
            {String(task.project).toUpperCase()}
          </Box>
        )}
        {task.type === 'milestone' && (
          <Box 
            component="span"
            sx={{
              display: 'inline-block',
              px: 1,
              py: 0.25,
              borderRadius: 1,
              fontSize: 12,
              lineHeight: 1.4,
              border: '1px solid',
              borderColor: 'divider'
            }}
          >
            Milestone
          </Box>
        )}
      </Box>

      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
          <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'uppercase', letterSpacing: 0.4 }}>
            Dates
          </Typography>
          <Typography variant="body2">
            {format(task.start, 'MMM d, yyyy')} – {format(task.end, 'MMM d, yyyy')}
          </Typography>
        </Box>
        {durationLabel && (
          <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
            <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'uppercase', letterSpacing: 0.4 }}>
              Duration
            </Typography>
            <Typography variant="body2">{durationLabel}</Typography>
          </Box>
        )}
      </Box>
    </Paper>
  );
};

const TaskEditDialog = ({ open, task, onClose, onSave, onDelete, projectTasks = [], productionPhases = [] }) => {
  const [editedTask, setEditedTask] = useState(task || {});
  const [saving, setSaving] = useState(false);
  const [confirmDeleteOpen, setConfirmDeleteOpen] = useState(false);
  const theme = useTheme();
  const isMilestone = editedTask.type === 'milestone';
  const durationDays = useMemo(() => {
    if (!editedTask.start || !editedTask.end) return '';
    const start = new Date(editedTask.start);
    const end = new Date(editedTask.end);
    const msPerDay = 24 * 60 * 60 * 1000;
    const diff = Math.max(0, Math.ceil((end.setHours(0,0,0,0) - start.setHours(0,0,0,0)) / msPerDay)) + 1;
    return isNaN(diff) ? '' : diff;
  }, [editedTask.start, editedTask.end]);

  // Initialize with default values if creating a new task
  useEffect(() => {
    if (!task) {
      // Set default values for new tasks
      setEditedTask({
        title: '',
        start: new Date(),
        end: new Date(new Date().setDate(new Date().getDate() + 7)), // Default to 1 week duration
        phase: '',
        progress: 0,
        type: 'task',
        description: '',
        status: 'not-started'
      });
    } else {
      // Map backend fields to editor state
      const start = task.start || task.startDate || task.start_time || task.start_at || task.startTime || task.start_at_date;
      const end = task.end || task.endDate || task.end_time || task.end_at || task.endTime || task.end_at_date;
      const isMilestone = (typeof task.isMilestone === 'boolean') ? task.isMilestone : (task.type === 'milestone');
      const normalized = {
        ...task,
        id: task.id || task._id, // ensure id present for updates
        start: start ? new Date(start) : undefined,
        end: end ? new Date(end) : undefined,
        type: isMilestone ? 'milestone' : (task.type || 'task')
      };

      setEditedTask(normalized);
    }
  }, [task]);

  const handleChange = (field) => (event) => {
    setEditedTask(prev => ({
      ...prev,
      [field]: event.target.value
    }));
  };

  const handleDateChange = (field) => (date) => {
    setEditedTask(prev => ({
      ...prev,
      [field]: date
    }));
  };

  const handleDurationChange = (event) => {
    const value = parseInt(event.target.value, 10);
    if (!editedTask.start || isNaN(value) || value <= 0) return;
    const start = new Date(editedTask.start);
    const end = new Date(start);
    end.setDate(end.getDate() + (value - 1));
    setEditedTask(prev => ({ ...prev, end }));
  };

  // Progress UI removed; default progress will remain 0 unless set elsewhere

  // Validate that end date is after start date
  const isEndDateValid = !editedTask.start || !editedTask.end || 
    new Date(editedTask.end) >= new Date(editedTask.start);

  return (
    <Dialog 
      open={open} 
      onClose={onClose} 
      maxWidth="md" 
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 1,
          border: `1px solid ${alpha(theme.palette.divider, 0.12)}`,
          overflow: 'hidden'
        }
      }}
    >
      <DialogTitle sx={{ 
        px: 3,
        py: 2,
        display: 'flex',
        alignItems: 'center',
        gap: 1,
        fontWeight: 600
      }}>
        {task ? (
          <>
            <EditIcon fontSize="small" color="primary" />
            <Typography 
              variant="h6" 
              component="div"
              sx={{
                fontWeight: 600
              }}
            >
              Edit Production Task
            </Typography>
          </>
        ) : (
          <>
            <AddIcon fontSize="small" color="primary" />
            <Typography 
              variant="h6" 
              component="div"
              sx={{
                fontWeight: 600
              }}
            >
              Create New Production Task
            </Typography>
          </>
        )}
      </DialogTitle>
      <DialogContent dividers>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
          {/* Basic Information Section */}
          <Box>
            <Typography variant="subtitle2" color="primary" gutterBottom sx={{ fontWeight: 'bold', mb: 1 }}>
              Basic Information
            </Typography>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              <TextField
                label="Task Title"
                value={editedTask.title || ''}
                onChange={handleChange('title')}
                fullWidth
                required
                placeholder="Enter a descriptive title for this task"
                helperText={!editedTask.title ? "Title is required" : ""}
                error={!editedTask.title}
                InputLabelProps={{ shrink: true }}
              />

              <FormControl fullWidth required error={!editedTask.phase}>
                <InputLabel id="phase-select-label">Production Phase</InputLabel>
                <Select
                  labelId="phase-select-label"
                  value={editedTask.phase || ''}
                  onChange={handleChange('phase')}
                  label="Production Phase"
                >
                  {productionPhases.map((p) => {
                    const phaseColor = getPhaseColor(p.color || '#1976d2', theme);
                    return (
                      <MenuItem key={p.id} value={p.id} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Box sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: phaseColor, border: '1px solid', borderColor: theme.palette.divider }} />
                        {p.name}
                      </MenuItem>
                    );
                  })}
                </Select>
                {!editedTask.phase && <FormHelperText>Production phase is required</FormHelperText>}
              </FormControl>
            </Box>
          </Box>

          {/* Schedule Section */}
          <Box>
            <Typography variant="subtitle2" color="primary" gutterBottom sx={{ fontWeight: 'bold', mb: 1 }}>
              Schedule
            </Typography>
            <LocalizationProvider dateAdapter={AdapterDateFns}>
              <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-start' }}>
                <DatePicker
                  label="Start Date"
                  value={editedTask.start || null}
                  onChange={handleDateChange('start')}
                  renderInput={(params) => 
                    <TextField 
                      {...params} 
                      fullWidth 
                      required 
                      error={!editedTask.start}
                      helperText={!editedTask.start ? "Start date is required" : ""}
                    />
                  }
                />
                <DatePicker
                  label="End Date"
                  value={editedTask.end || null}
                  onChange={handleDateChange('end')}
                  renderInput={(params) => 
                    <TextField 
                      {...params} 
                      fullWidth 
                      required 
                      error={!editedTask.end || !isEndDateValid}
                      helperText={!editedTask.end ? "End date is required" : !isEndDateValid ? "End date must be after start date" : ""}
                      disabled={isMilestone}
                    />
                  }
                  minDate={editedTask.start || undefined}
                />
                <TextField
                  label="Duration (days)"
                  type="number"
                  value={durationDays}
                  onChange={handleDurationChange}
                  inputProps={{ min: 1 }}
                  helperText="Adjusts end date based on duration"
                  sx={{ width: 180 }}
                  disabled={!editedTask.start || isMilestone}
                />
              </Box>
            </LocalizationProvider>
          </Box>

          {/* Details Section */}
          <Box>
            <Typography variant="subtitle2" color="primary" gutterBottom sx={{ fontWeight: 'bold', mb: 1 }}>
              Task Details
            </Typography>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              <FormControl fullWidth>
                <InputLabel id="task-type-label">Task Type</InputLabel>
                <Select
                  labelId="task-type-label"
                  value={editedTask.type || 'task'}
                  onChange={(e) => {
                    const nextType = e.target.value;
                    setEditedTask(prev => {
                      const next = { ...prev, type: nextType };
                      if (nextType === 'milestone') {
                        // For milestones, force end = start and progress = 0
                        next.end = next.start || prev.end || new Date();
                        next.progress = 0;
                      }
                      return next;
                    });
                  }}
                  label="Task Type"
                >
                  <MenuItem value="task">Standard Task</MenuItem>
                  <MenuItem value="milestone">Milestone</MenuItem>
                </Select>
                <FormHelperText>
                  {editedTask.type === 'milestone' ? 
                    "Milestones represent key events with no duration" : 
                    "Standard tasks have a duration and can track progress"}
                </FormHelperText>
              </FormControl>

              {/* Assigned To removed */}

              <FormControl fullWidth>
                <InputLabel id="status-label">Status</InputLabel>
                <Select
                  labelId="status-label"
                  value={editedTask.status || 'not-started'}
                  onChange={handleChange('status')}
                  label="Status"
                >
                  <MenuItem value="not-started">Not started</MenuItem>
                  <MenuItem value="in-prep">In prep</MenuItem>
                  <MenuItem value="in-progress">In progress</MenuItem>
                  <MenuItem value="on-hold">On hold</MenuItem>
                  <MenuItem value="blocked">Blocked</MenuItem>
                  <MenuItem value="completed">Completed</MenuItem>
                </Select>
                <FormHelperText>Production-friendly statuses</FormHelperText>
              </FormControl>

              <TextField
                label="Description / Notes"
                value={editedTask.description || ''}
                onChange={handleChange('description')}
                fullWidth
                multiline
                minRows={3}
                placeholder="Add context, key deliverables, scene numbers, locations, etc."
                InputLabelProps={{ shrink: true }}
              />
            </Box>
          </Box>
        </Box>
      </DialogContent>
      <DialogActions sx={{ 
        borderTop: `1px solid ${theme.palette.divider}`,
        px: 3,
        py: 2,
        display: 'flex',
        justifyContent: 'space-between'
      }}>
        <Box>
          {task && (
            <Button 
              onClick={() => setConfirmDeleteOpen(true)}
              color="error"
              startIcon={<DeleteIcon />}
              variant="outlined"
              disableElevation
              sx={{ textTransform: 'none', borderRadius: 1, px: 2 }}
            >
              Delete Task
            </Button>
          )}
        </Box>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button 
            onClick={onClose}
            variant="outlined"
            disableElevation
            sx={{ textTransform: 'none', borderRadius: 1, px: 2 }}
          >
            Cancel
          </Button>
          <Button 
            onClick={async () => {
              // Map to backend schema: use start/end and optional isMilestone (ignored by server)
              const formattedTask = {
                id: task?.id || task?._id || editedTask.id, // required for updates
                title: editedTask.title,
                start: editedTask.start,
                end: editedTask.end,
                phase: editedTask.phase,
                progress: editedTask.progress || 0,
                description: editedTask.description || '',
                status: editedTask.status || 'not-started',
                isMilestone: editedTask.type === 'milestone'
              };
              try {
                setSaving(true);
                const res = onSave(formattedTask);
                if (res && typeof res.then === 'function') {
                  await res;
                }
              } finally {
                setSaving(false);
              }
            }} 
            variant="contained"
            disabled={saving || !editedTask.title || !editedTask.start || !editedTask.end || !editedTask.phase || !isEndDateValid}
            startIcon={<CheckCircleIcon />}
            disableElevation
            sx={{ textTransform: 'none', borderRadius: 1, px: 2.5 }}
          >
            {saving ? 'Saving…' : (task ? 'Update Task' : 'Create Task')}
            {saving && <CircularProgress size={18} thickness={5} sx={{ ml: 1 }} />}
          </Button>
        </Box>
      </DialogActions>
      {/* Confirm Delete Dialog */}
      <Dialog open={confirmDeleteOpen} onClose={() => setConfirmDeleteOpen(false)}>
        <DialogTitle>Delete Task?</DialogTitle>
        <DialogContent>
          <Typography variant="body2">This action cannot be undone.</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmDeleteOpen(false)} variant="outlined" sx={{ textTransform: 'none', borderRadius: 1, px: 2 }}>Cancel</Button>
          <Button 
            onClick={() => { setConfirmDeleteOpen(false); onDelete && onDelete(); }} 
            color="error" 
            variant="contained" 
            sx={{ textTransform: 'none', borderRadius: 1, px: 2.5 }}
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </Dialog>
  );
};

const TemplateDialog = ({ open, onClose, onApply }) => {
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [startDate, setStartDate] = useState(new Date());
  const [applying, setApplying] = useState(false);
  const theme = useTheme();

  const handleApply = () => {
    if (!selectedTemplate) return;

    const template = productionTemplates[selectedTemplate];
    let currentDate = new Date(startDate);
    const tasks = [];

    template.phases.forEach(phase => {
      phase.tasks.forEach(task => {
        const taskStartDate = new Date(currentDate);
        const taskEndDate = new Date(currentDate);
        taskEndDate.setDate(taskEndDate.getDate() + task.duration);

        tasks.push({
          title: task.title,
          start: taskStartDate,
          end: taskEndDate,
          phase: phase.name.toLowerCase(),
          progress: 0,
          description: task.description || '',
          status: 'not-started'
        });

        currentDate = new Date(taskEndDate);
        currentDate.setDate(currentDate.getDate() + 1); // Add a day between tasks
      });
    });

    console.log('Created template tasks:', tasks); // Debug log
    onApply(tasks);
    onClose();
  };

  return (
    <Dialog 
      open={open} 
      onClose={onClose} 
      maxWidth="sm" 
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 1,
          border: `1px solid ${alpha(theme.palette.divider, 0.12)}`,
          overflow: 'hidden'
        }
      }}
    >
      <DialogTitle sx={{ px: 3, py: 2, fontWeight: 600 }}>
        <Typography 
          variant="h6" 
          component="div"
          sx={{
            fontWeight: 600
          }}
        >
          Apply Production Template
        </Typography>
      </DialogTitle>
      <DialogContent dividers>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 2 }}>
          <FormControl fullWidth>
            <InputLabel>Template</InputLabel>
            <Select
              value={selectedTemplate || ''}
              onChange={(e) => setSelectedTemplate(e.target.value)}
              label="Template"
            >
              {Object.entries(productionTemplates).map(([key, template]) => (
                <MenuItem key={key} value={key}>
                  {template.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <LocalizationProvider dateAdapter={AdapterDateFns}>
            <DatePicker
              label="Start Date"
              value={startDate}
              onChange={setStartDate}
              renderInput={(params) => <TextField {...params} fullWidth />}
            />
          </LocalizationProvider>

          {selectedTemplate && (
            <Box sx={{ mt: 2 }}>
              <Typography variant="subtitle2" gutterBottom>
                Template Details:
              </Typography>
              <List dense>
                {productionTemplates[selectedTemplate].phases.map((phase) => (
                  <ListItem key={phase.name}>
                    <ListItemText
                      primary={phase.name}
                      secondary={`${phase.tasks.length} tasks`}
                    />
                  </ListItem>
                ))}
              </List>
            </Box>
          )}
        </Box>
      </DialogContent>
      <DialogActions sx={{ p: 2, pt: 0 }}>
        <Button 
          onClick={onClose} 
          variant="outlined"
          disableElevation
          disabled={applying}
          sx={{ textTransform: 'none', borderRadius: 1, px: 2 }}
        >
          Cancel
        </Button>
        <Button
          onClick={async () => {
            try {
              setApplying(true);
              await Promise.resolve(handleApply());
            } finally {
              setApplying(false);
            }
          }}
          variant="contained"
          disabled={applying || !selectedTemplate}
          disableElevation
          sx={{ textTransform: 'none', borderRadius: 1, px: 2.5 }}
        >
          {applying ? 'Applying…' : 'Apply Template'}
          {applying && <CircularProgress size={18} thickness={5} sx={{ ml: 1 }} />}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

const GanttChart = ({ 
  projectTasks = [], 
  onAddTask, 
  onUpdateTask, 
  onDeleteTask, 
  onClearAllTasks, 
  productionPhases = [],
  initialSelectedPhases = []
 }) => {
  const theme = useTheme();
  const [currentViewMode, setCurrentViewMode] = useState(ViewMode.Month);
  const [zoom, setZoom] = useState(100);
  const [selectedPhases, setSelectedPhases] = useState(initialSelectedPhases.length > 0 ? initialSelectedPhases : productionPhases.map(phase => phase.id));
  const [searchText, setSearchText] = useState('');
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [templateDialogOpen, setTemplateDialogOpen] = useState(false);
  const [selectedTask, setSelectedTask] = useState(null);

  // LocalStorage keys
  const STORAGE_KEYS = {
    viewMode: 'gantt_viewMode',
    zoom: 'gantt_zoom',
    phases: 'gantt_selectedPhases',
    search: 'gantt_searchText',
  };

  // Helpers to persist and load ViewMode across numeric/string enum variants
  const viewModeKey = (mode) => {
    if (mode === ViewMode.Day) return 'Day';
    if (mode === ViewMode.Week) return 'Week';
    if (mode === ViewMode.Month) return 'Month';
    return 'Month';
  };
  const viewModeFromStored = (val) => {
    if (!val) return null;
    // Prefer key lookup if available
    if (ViewMode && ViewMode[val] !== undefined) return ViewMode[val];
    // Fallback: compare against enum values coerced to string
    if (String(ViewMode.Day) === val) return ViewMode.Day;
    if (String(ViewMode.Week) === val) return ViewMode.Week;
    if (String(ViewMode.Month) === val) return ViewMode.Month;
    return null;
  };

  // Load persisted UI state
  useEffect(() => {
    try {
      const storedVM = localStorage.getItem(STORAGE_KEYS.viewMode);
      const vm = viewModeFromStored(storedVM);
      if (vm) setCurrentViewMode(vm);
    } catch {}
    try {
      const storedZoom = localStorage.getItem(STORAGE_KEYS.zoom);
      if (storedZoom) {
        const z = parseInt(storedZoom, 10);
        if (!isNaN(z)) setZoom(Math.min(Math.max(z, 50), 150));
      }
    } catch {}
    try {
      const storedSearch = localStorage.getItem(STORAGE_KEYS.search);
      if (storedSearch !== null) setSearchText(storedSearch);
    } catch {}
  }, []);

  // Load phases when available
  useEffect(() => {
    if (!productionPhases || productionPhases.length === 0) return;
    try {
      const raw = localStorage.getItem(STORAGE_KEYS.phases);
      if (!raw) return;
      const parsed = JSON.parse(raw);
      if (Array.isArray(parsed)) {
        const valid = new Set(productionPhases.map(p => p.id));
        const next = parsed.filter(id => valid.has(id));
        if (next.length) setSelectedPhases(next);
      }
    } catch {}
  }, [productionPhases]);

  // Persist UI state
  useEffect(() => {
    try { localStorage.setItem(STORAGE_KEYS.viewMode, viewModeKey(currentViewMode)); } catch {}
  }, [currentViewMode]);
  useEffect(() => {
    try { localStorage.setItem(STORAGE_KEYS.zoom, String(zoom)); } catch {}
  }, [zoom]);
  useEffect(() => {
    try { localStorage.setItem(STORAGE_KEYS.search, searchText); } catch {}
  }, [searchText]);
  useEffect(() => {
    try { localStorage.setItem(STORAGE_KEYS.phases, JSON.stringify(selectedPhases)); } catch {}
  }, [selectedPhases]);
  
  // Handle toggling phases on/off for filtering
  const handlePhaseToggle = (phaseId) => {
    setSelectedPhases(prev => {
      // If this is the only selected phase, don't allow deselecting it
      if (prev.length === 1 && prev.includes(phaseId)) {
        return prev;
      }
      
      // Toggle the phase
      return prev.includes(phaseId)
        ? prev.filter(id => id !== phaseId)
        : [...prev, phaseId];
    });
  };
  
  // Handle selecting all phases
  const handleSelectAllPhases = () => {
    setSelectedPhases(productionPhases.map(phase => phase.id));
  };
  
  // Handle clearing phase selection (except one)
  const handleClearPhases = () => {
    // Keep only the first phase selected to avoid empty view
    if (productionPhases.length > 0) {
      setSelectedPhases([productionPhases[0].id]);
    }
  };

  // This function has been replaced with an improved version above

  const handleViewModeChange = (mode) => {
    setCurrentViewMode(mode);
  };

  const handleZoomChange = (newZoom) => {
    setZoom(Math.min(Math.max(newZoom, 50), 150));
  };

  // Get column width based on view mode and zoom
  const getColumnWidth = () => {
    const baseWidth = {
      [ViewMode.Day]: 60,
      [ViewMode.Week]: 120,
      [ViewMode.Month]: 200,
    };
    return (baseWidth[currentViewMode] * zoom) / 100;
  };

  // Helper function to validate and convert dates
  const convertToDate = (date) => {
    if (!date) return null;
    
    // If it's already a Date object
    if (date instanceof Date) return date;
    
    // If it's a string, try to parse it
    if (typeof date === 'string') {
      const parsed = new Date(date);
      return isValidDate(parsed) ? parsed : null;
    }
    
    // If it has start property (gantt format)
    if (date.start instanceof Date) return date.start;
    if (typeof date.start === 'string') {
      const parsed = new Date(date.start);
      return isValidDate(parsed) ? parsed : null;
    }
    
    // If it has startDate property (kanban format)
    if (date.startDate instanceof Date) return date.startDate;
    if (typeof date.startDate === 'string') {
      const parsed = new Date(date.startDate);
      return isValidDate(parsed) ? parsed : null;
    }
    
    return null;
  };

  // Helper function to validate dates
  const isValidDate = (date) => {
    return date instanceof Date && !isNaN(date) && date.getTime() > 0;
  };

  // Get the date range for the timeline
  const getTimelineDates = () => {
    const today = new Date();
    const defaultStart = today;
    const defaultEnd = new Date(today);
    defaultEnd.setMonth(defaultEnd.getMonth() + 3);

    if (!projectTasks || !Array.isArray(projectTasks) || projectTasks.length === 0) {
      return { minDate: defaultStart, maxDate: defaultEnd };
    }

    try {
      const validTasks = projectTasks.filter(task => {
        const start = convertToDate(task);
        const end = convertToDate({ ...task, start: task.end || task.endDate });
        return start && end;
      });

      if (validTasks.length === 0) {
        return { minDate: defaultStart, maxDate: defaultEnd };
      }

      const dates = validTasks.flatMap(task => [
        convertToDate(task),
        convertToDate({ ...task, start: task.end || task.endDate })
      ]);

      const minDate = new Date(Math.min(...dates.map(d => d.getTime())));
      const maxDate = new Date(Math.max(...dates.map(d => d.getTime())));

      // Ensure we have valid dates
      if (!isValidDate(minDate) || !isValidDate(maxDate)) {
        return { minDate: defaultStart, maxDate: defaultEnd };
      }

      // Add some padding to the date range
      const start = new Date(minDate);
      start.setMonth(start.getMonth() - 1);
      const end = new Date(maxDate);
      end.setMonth(end.getMonth() + 1);

      return { minDate: start, maxDate: end };
    } catch (error) {
      console.error('Error calculating timeline dates:', error);
      return { minDate: defaultStart, maxDate: defaultEnd };
    }
  };

  const { minDate, maxDate } = useMemo(() => getTimelineDates(), [projectTasks]);

  // Convert tasks to Gantt format
  const ganttTasks = useMemo(() => {
    if (!projectTasks || !Array.isArray(projectTasks) || projectTasks.length === 0) {
      return [];
    }

    // Create a map to track used IDs and ensure uniqueness
    const usedIds = new Map();

    // Build tasks with stable original IDs
    const initial = projectTasks
      .filter(task => {
        if (!task || typeof task !== 'object') {
          console.warn('Invalid task object:', task);
          return false;
        }

        const start = convertToDate(task);
        const end = convertToDate({ ...task, start: task.end || task.endDate });
        
        if (!start || !end) {
          console.warn('Task missing valid start or end date:', task);
          return false;
        }

        return true;
      })
      .map((task, index) => {
        try {
          const start = convertToDate(task);
          const end = convertToDate({ ...task, start: task.end || task.endDate });

          // Generate a base ID from the task
          let baseId = task.id?.toString() || task._id?.toString() || Date.now().toString();
          
          // Ensure the ID is unique by adding an index suffix if needed
          let uniqueId = baseId;
          if (usedIds.has(baseId)) {
            uniqueId = `${baseId}_${index}`;
          }
          usedIds.set(uniqueId, true);

          const isMs = !!task.isMilestone || task.type === 'milestone';
          return {
            start,
            end,
            name: task.title || task.name || 'Untitled Task',
            id: uniqueId, // Use the guaranteed unique ID
            originalId: baseId, // Keep the original ID for reference
            type: isMs ? 'milestone' : 'task',
            progress: task.progress || 0,
            styles: {
              backgroundColor: getPhaseColor(productionPhases.find(p => p.id === (task.phase || task.project))?.color || '#1976d2', theme),
              progressColor: '#ffffff',
              progressSelectedColor: '#ffffff',
              backgroundSelectedColor: isMs ? '#f50057' : undefined,
            },
            project: task.phase || task.project || 'production',
            hideChildren: false,
            displayOrder: task.displayOrder || task.order || 0
          };
        } catch (error) {
          console.error('Error processing task:', task, error);
          return null;
        }
      })
      .filter(Boolean);

    return initial;
  }, [projectTasks, productionPhases, theme]);

  // Filter tasks based on selected phases
  const filteredTasks = useMemo(() => {
    const matchesSearch = (name) => {
      if (!searchText) return true;
      const needle = searchText.trim().toLowerCase();
      return (name || '').toLowerCase().includes(needle);
    };

    return ganttTasks.filter(task => {
      const inPhase = (task && task.project && selectedPhases.includes(task.project)) || task.type === 'project';
      const passSearch = matchesSearch(task?.name);
      return inPhase && passSearch;
    });
  }, [ganttTasks, selectedPhases, searchText]);

  // Group tasks by phase
  const groupedTasks = useMemo(() => {
    if (!filteredTasks || filteredTasks.length === 0) {
      return [{
        start: minDate,
        end: maxDate,
        name: "No Tasks",
        id: "empty-project",
        type: "project",
        progress: 0,
        isDisabled: true,
        hideChildren: false,
        displayOrder: 0,
        project: "empty"
      }];
    }

    return productionPhases.flatMap(phase => {
      const phaseTasks = filteredTasks.filter(task => task.project === phase.id);
      if (!phaseTasks || phaseTasks.length === 0) return [];

      try {
        const validDates = phaseTasks
          .map(task => ({ start: task.start, end: task.end }))
          .filter(dates => isValidDate(dates.start) && isValidDate(dates.end));

        if (validDates.length === 0) return [];

        const phaseStart = new Date(Math.min(...validDates.map(d => d.start.getTime())));
        const phaseEnd = new Date(Math.max(...validDates.map(d => d.end.getTime())));

        return [
          {
            start: phaseStart,
            end: phaseEnd,
            name: phase.name,
            id: `phase-${phase.id}`,
            type: 'project',
            progress: 0,
            isDisabled: true,
            styles: { 
              backgroundColor: getPhaseColor(phase.color, theme),
              backgroundSelectedColor: getPhaseColor(phase.color, theme),
              progressColor: '#ffffff',
              progressSelectedColor: '#ffffff'
            },
            project: phase.id,
            hideChildren: false,
            displayOrder: 0,
          },
          ...phaseTasks
        ];
      } catch (error) {
        console.error(`Error processing phase ${phase.id}:`, error);
        return [];
      }
    }).filter(Boolean);
  }, [filteredTasks, productionPhases, minDate, maxDate, theme]);

  const handleTaskDragEnd = (result) => {
    if (!result.destination || result.type !== 'TASK') return;

    const sourceIndex = result.source.index;
    const destinationIndex = result.destination.index;
    
    // If the task didn't actually move, don't do anything
    if (sourceIndex === destinationIndex) return;

    // Find the task that was moved by its ID from the drag result
    const taskId = result.draggableId;
    const movedTask = projectTasks.find(task => {
      // The draggableId might be the unique ID we generated for React
      // So we need to check if the task's ID matches or if it was generated with a suffix
      const baseTaskId = task.id?.toString() || task._id?.toString();
      return baseTaskId === taskId || taskId.startsWith(baseTaskId + '_');
    });
    
    if (!movedTask || movedTask.type === 'project') return;
    
    // Only update the moved task with its new display order
    const updatedMovedTask = {
      ...movedTask,
      displayOrder: destinationIndex,
      // Make sure we're using the original MongoDB ID for the API call
      id: movedTask.originalId || movedTask.id
    };
    
    console.log('Moving task:', updatedMovedTask.id, 'from', sourceIndex, 'to', destinationIndex);
    
    // Call onUpdateTask only once for the moved task
    onUpdateTask(updatedMovedTask);
    
    // Note: For a more comprehensive solution, consider implementing a batch update API
    // endpoint to update all task display orders in a single request
  };

  const handleTaskClick = (task) => {
    if (task.type !== 'project') {
      // Find the original task data from projectTasks using the ID or originalId
      const taskId = task.originalId || task.id;
      const originalTask = projectTasks.find(t => 
        (t.id?.toString() === taskId?.toString() || t._id?.toString() === taskId?.toString())
      );
      
      if (originalTask) {
        // Use the original task data with proper field mappings
        const isMilestone = (typeof originalTask.isMilestone === 'boolean')
          ? originalTask.isMilestone
          : (originalTask.type === 'milestone');
        const mappedTask = {
          id: taskId,
          title: originalTask.title || originalTask.name,
          start: originalTask.startDate || originalTask.start || task.start,
          end: originalTask.endDate || originalTask.end || task.end,
          phase: originalTask.phase || originalTask.project || task.project,
          progress: originalTask.progress || task.progress || 0,
          description: originalTask.description || '',
          status: originalTask.status || 'not-started',
          type: isMilestone ? 'milestone' : 'task',
          // Keep the originalId for reference
          originalId: taskId
        };
        
        setSelectedTask(mappedTask);
      } else {
        // Fallback to the Gantt chart task data if original not found
        const mappedTask = {
          id: task.id,
          title: task.name,
          start: task.start,
          end: task.end,
          phase: task.project,
          progress: task.progress || 0,
          description: '',
          status: 'not-started',
          type: task.type,
          originalId: task.id
        };
        
        setSelectedTask(mappedTask);
      }
      
      setEditDialogOpen(true);
    }
  };

  const handleApplyTemplate = async (templateTasks) => {
    console.log('Applying template tasks:', templateTasks); // Debug log
    try {
      const creations = templateTasks.map(task => {
        const newTask = {
          title: task.title,
          start: task.start,
          end: task.end,
          phase: task.phase,
          progress: 0,
          status: 'not-started',
          description: task.description || '',
          // Preserve milestone intent for UI after create
          isMilestone: task.type === 'milestone'
        };
        console.log('Adding template task:', newTask); // Debug log
        const result = onAddTask(newTask);
        return (result && typeof result.then === 'function') ? result : Promise.resolve(result);
      });
      await Promise.all(creations);
    } catch (e) {
      console.error('Error adding template tasks:', e);
    } finally {
      setTemplateDialogOpen(false);
    }
  };

  const renderControls = () => (
    <Box sx={{ 
      display: 'flex', 
      flexDirection: 'column',
      gap: 1,
      borderBottom: `1px solid ${theme.palette.divider}`,
      backgroundColor: theme.palette.mode === 'dark' ? theme.palette.background.default : '#f5f5f5',
      p: 2,
      position: 'sticky',
      top: 0,
      zIndex: theme.zIndex.appBar,
      boxShadow: theme.shadows[1]
    }}>
      {/* Main Controls */}
      <Box sx={{ 
        display: 'flex', 
        alignItems: 'center', 
        gap: 2,
        flexWrap: 'wrap'
      }}>
        <Tooltip title="Apply Template">
          <Button
            variant="outlined"
            size="small"
            startIcon={<PlaylistAddIcon />}
            onClick={() => setTemplateDialogOpen(true)}
            aria-label="apply-template"
            disableElevation
            sx={{ textTransform: 'none', borderRadius: 1, px: 2 }}
          >
            Template
          </Button>
        </Tooltip>

        {/* Wrap disabled button in span to fix Tooltip warning */}
        <Tooltip title="Clear All Tasks">
          {(!onClearAllTasks || projectTasks.length === 0) ? (
            <span>
              <Button
                variant="outlined"
                size="small"
                color="error"
                startIcon={<DeleteSweepIcon />}
                disabled={true}
                aria-label="clear-all-disabled"
                disableElevation
                sx={{ textTransform: 'none', borderRadius: 1, px: 2 }}
              >
                Clear All
              </Button>
            </span>
          ) : (
            <Button
              variant="outlined"
              size="small"
              color="error"
              startIcon={<DeleteSweepIcon />}
              onClick={() => {
                // Show confirmation dialog before clearing all tasks
                if (window.confirm('Are you sure you want to clear all tasks? This action cannot be undone.')) {
                  onClearAllTasks();
                }
              }}
              aria-label="clear-all"
              disableElevation
              sx={{ textTransform: 'none', borderRadius: 1, px: 2 }}
            >
              Clear All
            </Button>
          )}
        </Tooltip>

        <ToggleButtonGroup
          size="small"
          exclusive
          value={currentViewMode}
          onChange={(e, val) => val && handleViewModeChange(val)}
          aria-label="view-mode"
        >
          <ToggleButton value={ViewMode.Day} aria-label="day-view" sx={{ borderRadius: 1, textTransform: 'none' }}>
            <CalendarViewDayIcon fontSize="small" />
          </ToggleButton>
          <ToggleButton value={ViewMode.Week} aria-label="week-view" sx={{ borderRadius: 1, textTransform: 'none' }}>
            <CalendarViewWeekIcon fontSize="small" />
          </ToggleButton>
          <ToggleButton value={ViewMode.Month} aria-label="month-view" sx={{ borderRadius: 1, textTransform: 'none' }}>
            <CalendarViewMonthIcon fontSize="small" />
          </ToggleButton>
        </ToggleButtonGroup>

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 220 }}>
          <Typography variant="caption" color="text.secondary">Zoom</Typography>
          <Slider
            size="small"
            value={zoom}
            onChange={(e, val) => handleZoomChange(val)}
            min={50}
            max={150}
            step={10}
            sx={{ width: 140 }}
            aria-label="zoom-slider"
          />
          <Chip label={`${zoom}%`} size="small" />
        </Box>

        <TextField
          size="small"
          placeholder="Search tasks..."
          value={searchText}
          onChange={(e) => setSearchText(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon fontSize="small" />
              </InputAdornment>
            ),
          }}
          sx={{ minWidth: 220, flex: 1, maxWidth: 380 }}
          aria-label="search-tasks"
        />

        {/* Today button removed as requested */}

        <Box sx={{ flex: 1 }} />

        <Button
          variant="contained"
          size="small"
          startIcon={<AddIcon />}
          onClick={() => {
            setSelectedTask(null);
            setEditDialogOpen(true);
          }}
          aria-label="add-task"
          disableElevation
          sx={{ textTransform: 'none', borderRadius: 1, px: 2.5 }}
        >
          Add Task
        </Button>
      </Box>

      {/* Phase Filters */}
      <Box sx={{ 
        display: 'flex', 
        flexDirection: 'column',
        gap: 1,
        mt: 1,
        p: 1,
        borderTop: `1px solid ${theme.palette.divider}`,
      }}>
        <Typography variant="subtitle2" fontWeight="bold" sx={{ mb: 0.5 }}>
          Production Phases
        </Typography>
        
        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'center' }}>
          {productionPhases.map(phase => {
            const isSelected = selectedPhases.includes(phase.id);
            const phaseColor = getPhaseColor(phase.color, theme);

            const softBg = theme.palette.mode === 'dark' 
              ? `rgba(${parseInt(phaseColor.slice(1, 3), 16)}, ${parseInt(phaseColor.slice(3, 5), 16)}, ${parseInt(phaseColor.slice(5, 7), 16)}, 0.18)`
              : `${phaseColor}1A`; // ~10% alpha

            return (
              <Tooltip key={phase.id} title={phase.description || phase.name} arrow>
                <Chip
                  label={phase.name}
                  size="small"
                  clickable
                  onClick={() => handlePhaseToggle(phase.id)}
                  icon={isSelected ? <CheckCircleIcon fontSize="small" /> : undefined}
                  aria-label={`toggle-phase-${phase.id}`}
                  aria-pressed={isSelected}
                  variant={isSelected ? 'filled' : 'outlined'}
                  sx={{
                    borderRadius: 9999,
                    borderWidth: 1,
                    borderStyle: 'solid',
                    borderColor: phaseColor,
                    backgroundColor: isSelected ? softBg : 'transparent',
                    color: isSelected ? theme.palette.text.primary : theme.palette.text.primary,
                    fontWeight: 600,
                    letterSpacing: 0.2,
                    transition: 'background-color 0.2s ease, box-shadow 0.2s ease, transform 0.1s ease',
                    '& .MuiChip-icon': {
                      color: phaseColor,
                    },
                    '&:hover': {
                      backgroundColor: isSelected ? softBg : (theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.04)' : 'rgba(0,0,0,0.02)'),
                      boxShadow: theme.shadows[1],
                      transform: 'translateY(-1px)'
                    },
                    '&:active': {
                      transform: 'translateY(0)'
                    },
                    '&.Mui-focusVisible': {
                      outline: `2px solid ${phaseColor}`,
                      outlineOffset: 2,
                    },
                  }}
                />
              </Tooltip>
            );
          })}

          {/* Quick actions */}
          <Box sx={{ display: 'flex', gap: 1, ml: 'auto' }}>
            <Button size="small" variant="text" onClick={handleSelectAllPhases} aria-label="select-all-phases" sx={{ textTransform: 'none', borderRadius: 1, px: 1.5 }}>Select All</Button>
            <Button size="small" variant="text" onClick={handleClearPhases} aria-label="clear-phases" sx={{ textTransform: 'none', borderRadius: 1, px: 1.5 }}>Clear</Button>
          </Box>
        </Box>
      </Box>
    </Box>
  );

  const renderTaskTooltip = (task) => (
    <TaskTooltip task={task} />
  );

  const TaskListHeader = () => {
    // Fixed header height that exactly matches the Gantt chart header
    return (
      <div
        style={{
          padding: '8px 16px',
          height: '40px', // Match the Gantt chart header height
          display: 'flex',
          alignItems: 'center',
          borderBottom: `1px solid ${theme.palette.divider}`,
          fontWeight: 'bold',
          backgroundColor: theme.palette.mode === 'dark' ? '#333' : '#f5f5f5',
          boxSizing: 'border-box',
        }}
      >
        Tasks
      </div>
    );
  };

  // Table renderer for the left task list; keeps row heights aligned with chart
  const TaskListTable = ({ tasks }) => {
    const ROW_HEIGHT = 38; // Match rowHeight passed to Gantt
    return (
      <div className="task-list-wrapper">
        {tasks.map((task) => (
          <div
            key={task.id}
            style={{
              height: `${ROW_HEIGHT}px`,
              display: 'flex',
              alignItems: 'center',
              padding: '0 16px',
              borderBottom: `1px solid ${theme.palette.divider}`,
              cursor: 'pointer',
              backgroundColor: theme.palette.background.paper,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
              boxSizing: 'border-box',
              fontSize: '0.875rem',
            }}
            onClick={() => handleTaskClick(task)}
            title={task.name}
          >
            {task.name}
          </div>
        ))}
      </div>
    );
  };

  const renderTaskList = () => {
    return (
      <div>
        <div 
          style={{ 
            padding: '8px 16px',
            backgroundColor: theme.palette.mode === 'dark' ? theme.palette.background.default : '#f5f5f5',
            borderBottom: `1px solid ${theme.palette.divider}`,
            fontWeight: 500
          }}
        >
          Task List
        </div>
        {projectTasks.map(task => (
          <div
            key={task.id}
            style={{
              padding: '8px 16px',
              borderBottom: `1px solid ${theme.palette.divider}`,
              cursor: 'pointer',
            }}
            onClick={() => handleTaskClick(task)}
          >
            {task.name}
          </div>
        ))}
      </div>
    );
  };

  // Drag-to-pan (horizontal) for the timeline container
  const scrollRef = useRef(null); // scrollable Box around Gantt
  const containerRef = useRef(null); // container for height measurement
  const isPanningRef = useRef(false);
  const startXRef = useRef(0);
  const scrollLeftRef = useRef(0);
  const [isPanning, setIsPanning] = useState(false);
  const panTargetsRef = useRef([]);
  const [containerHeight, setContainerHeight] = useState(window.innerHeight - 320);

  const handleMouseDown = (e) => {
    const el = scrollRef.current;
    if (!el) return;
    if (e.button !== 0) return; // left click only
    isPanningRef.current = true;
    setIsPanning(true);
    startXRef.current = e.clientX;
    // Collect all horizontal scrollable targets within the container
    const candidates = Array.from(el.querySelectorAll('*'));
    const targets = [];
    for (const node of candidates) {
      try {
        const style = window.getComputedStyle(node);
        const canScrollX = (node.scrollWidth || 0) > (node.clientWidth || 0);
        const overX = style.overflowX;
        if (canScrollX && overX !== 'visible') {
          targets.push({ el: node, startLeft: node.scrollLeft });
        }
      } catch {}
    }
    // Fallback: include the host if applicable
    if ((el.scrollWidth || 0) > (el.clientWidth || 0)) {
      targets.push({ el, startLeft: el.scrollLeft });
    }
    panTargetsRef.current = targets;
    scrollLeftRef.current = el.scrollLeft;
    // Prevent text selection while panning
    e.preventDefault();
    // End pan even if mouseup occurs outside the element
    const onUp = () => {
      endPan();
      document.removeEventListener('mouseup', onUp);
    };
    document.addEventListener('mouseup', onUp);
  };

  const handleMouseMove = (e) => {
    if (!isPanningRef.current) return;
    const x = e.clientX;
    const walk = (x - startXRef.current) * 1.2; // multiplier adjusts pan speed
    // Apply to all scrollable targets
    const targets = panTargetsRef.current || [];
    if (targets.length) {
      targets.forEach(({ el, startLeft }) => {
        try { el.scrollLeft = startLeft - walk; } catch {}
      });
    } else {
      const el = scrollRef.current;
      if (el) el.scrollLeft = scrollLeftRef.current - walk;
    }
    e.preventDefault();
  };

  const endPan = () => {
    if (isPanningRef.current) {
      isPanningRef.current = false;
      setIsPanning(false);
    }
  };

  const handleWheel = (e) => {
    const el = scrollRef.current;
    if (!el) return;
    // Hold Shift to scroll horizontally with the wheel
    if (e.shiftKey) {
      el.scrollLeft += (e.deltaY !== 0 ? e.deltaY : e.deltaX);
      e.preventDefault();
    }
  };

// Measure container height and update on resize
useEffect(() => {
  const updateHeight = () => {
    if (containerRef.current) {
      const rect = containerRef.current.getBoundingClientRect();
      setContainerHeight(Math.max(320, rect.height - 60)); // Leave space for controls
    } else {
      setContainerHeight(window.innerHeight - 320);
    }
  };

  updateHeight();
  window.addEventListener('resize', updateHeight);
  return () => window.removeEventListener('resize', updateHeight);
}, []);

// Height for the Gantt component
const computedHeight = useMemo(() => containerHeight, [containerHeight]);

return (
  <DragDropContext onDragEnd={handleTaskDragEnd}>
    <Paper sx={getCustomStyles(theme).wrapper}>
      {renderControls()}
      
      <Box
        ref={containerRef}
        sx={{
          width: '100%',
          flex: 1,
          display: 'flex',
          flexDirection: 'column',
          minHeight: 0
        }}>
          {/* Gantt Chart */}
          <Box
            ref={scrollRef}
            onMouseDownCapture={handleMouseDown}
            onMouseMoveCapture={handleMouseMove}
            onMouseUp={endPan}
            onMouseLeave={endPan}
            onWheel={handleWheel}
            sx={{
            flex: 1,
            overflowX: 'auto',
            overflowY: 'auto',
            minHeight: 0,
            cursor: isPanning ? 'grabbing' : 'grab',
            userSelect: isPanning ? 'none' : 'auto',
            '& .gantt-task-react': {
              background: theme.palette.background.paper,
              // Let height be controlled by ganttHeight prop
            },
            '& .gantt-task-react__chart': {
              background: theme.palette.background.paper,
            },
            /* Strict task list alignment */
            '& .gantt-task-react__task-list': {
              background: theme.palette.background.paper,
              borderRight: `1px solid ${theme.palette.divider}`,
            },
            /* Fix row heights and alignment */
            '& .gantt-task-react__task-list-item': {
              height: '38px !important', /* Force consistent height */
              boxSizing: 'border-box',
            },
            /* Fix alignment issues */
            '& .gantt-task-react__task-item': {
              height: '38px !important',
              display: 'flex !important',
              alignItems: 'center !important',
            },
            /* Ensure chart grid rows match precisely */
            '& .gantt-task-react__chart-row': {
              height: '38px !important',
            },
            '& .gantt-task-react__chart-timeline-grid-row': {
              borderBottom: `1px solid ${theme.palette.divider}`,
              height: '38px !important', /* Match the task row height exactly */
              backgroundColor: theme.palette.background.paper,
            },
            /* Fix header alignment */
            '& .gantt-task-react__calendar-header': {
              height: '40px !important',
              lineHeight: '40px !important',
            },
            /* Ensure month labels are aligned */
            '& .gantt-task-react__calendar-header-item': {
              height: '40px !important',
              lineHeight: '40px !important',
              display: 'flex !important',
              alignItems: 'center !important',
              justifyContent: 'center !important',
            },
            '& .gantt-task-react__task-list': {
              background: theme.palette.background.paper,
              color: theme.palette.text.primary,
              borderRight: `1px solid ${theme.palette.divider}`,
              '& .expanded': {
                backgroundColor: theme.palette.mode === 'dark' ? theme.palette.background.default : '#f5f5f5',
              }
            },
            '& .gantt-task-react__task-list-header': {
              background: theme.palette.background.paper,
              color: theme.palette.text.primary,
              fontWeight: 'bold',
              borderBottom: `1px solid ${theme.palette.divider}`,
              height: '50px',
              lineHeight: '50px',
            },
            '& .gantt-task-react__task-list-item': {
              borderBottom: `1px solid ${theme.palette.divider}`,
              height: '40px',
              lineHeight: '40px',
            },
            '& .gantt-task-react__task': {
              height: '35px',
              borderRadius: '4px',
              marginTop: '2.5px',
              '&.project': {
                backgroundColor: theme.palette.mode === 'dark' ? '#66BB6A !important' : '#4CAF50 !important',
                borderRadius: '3px',
              },
              '&.milestone': {
                backgroundColor: theme.palette.mode === 'dark' ? '#FF5983 !important' : '#f50057 !important',
                transform: 'rotate(45deg)',
                width: '20px !important',
                height: '20px !important',
                marginTop: '10px',
                borderRadius: '2px',
                border: `2px solid ${theme.palette.mode === 'dark' ? '#2b2b2b' : '#ffffff'}`,
              }
            },
            '& .gantt-task-react__task-progress': {
              backgroundColor: theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.45)' : 'rgba(255, 255, 255, 0.35)',
            },
            '& .gantt-task-react__chart-timeline': {
              backgroundColor: theme.palette.background.paper,
            },
            '& .gantt-task-react__chart-timeline-header': {
              backgroundColor: theme.palette.background.paper,
              borderBottom: `2px solid ${theme.palette.divider}`,
              height: '50px',
            },
            '& .gantt-task-react__chart-timeline-header-tick': {
              borderLeft: `1px solid ${theme.palette.divider}`,
            },
            '& .gantt-task-react__chart-timeline-header-tick-label': {
              color: theme.palette.text.primary,
              fontSize: '14px',
              fontWeight: 'bold',
              textTransform: 'uppercase',
            },
            '& .gantt-task-react__chart-timeline-grid': {
              backgroundColor: theme.palette.background.paper,
            },
            '& .gantt-task-react__chart-timeline-grid-row:nth-of-type(odd)': {
              backgroundColor: theme.palette.background.paper,
            },
            '& .gantt-task-react__chart-timeline-grid-row:nth-of-type(even)': {
              backgroundColor: theme.palette.mode === 'dark' ? theme.palette.background.default : '#f5f5f5',
            },
            '& .gantt-task-react__chart-timeline-grid-row-cell': {
              backgroundColor: 'transparent',
              borderRight: `1px solid ${theme.palette.divider}`,
            },
            '& .gantt-task-react__task-dependency': {
              borderColor: theme.palette.primary.main,
              '&::before': {
                borderColor: theme.palette.primary.main,
              }
            }
          }}>
            {filteredTasks.length === 0 ? (
              <Box sx={{
                height: '100%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                flexDirection: 'column',
                gap: 1.5,
                color: theme.palette.text.secondary,
              }}>
                <FlagIcon color="disabled" sx={{ fontSize: 48 }} />
                <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>No tasks to display</Typography>
                <Typography variant="body2" color="text.secondary">
                  Adjust filters or add a new task to get started.
                </Typography>
                <Button
                  variant="contained"
                  size="small"
                  startIcon={<AddIcon />}
                  onClick={() => {
                    setSelectedTask(null);
                    setEditDialogOpen(true);
                  }}
                >
                  Add Task
                </Button>
              </Box>
            ) : (
              <Gantt
              tasks={groupedTasks}
              viewMode={currentViewMode}
              onDateChange={(task, start, end) => {
                if (task.type !== 'project') {
                  onUpdateTask({ ...task, startDate: start, endDate: end });
                }
              }}
              onProgressChange={(task, progress) => {
                if (task.type !== 'project') {
                  onUpdateTask({ ...task, progress });
                }
              }}
              onDoubleClick={handleTaskClick}
              listCellWidth={250}
              ganttHeight={computedHeight}
              columnWidth={getColumnWidth()}
              barFill={80}
              barCornerRadius={4}
              handleWidth={8}
              rtl={false}
              locale="en-US"
              rowHeight={38} /* Consistent row height */
              fontSize={14}
              headerHeight={40} /* Match task list header */
              todayColor="rgba(25, 118, 210, 0.1)"
              viewDate={minDate}
              startDate={minDate}
              endDate={maxDate}
              headerBackgroundColor={theme.palette.background.paper}
              headerBorderColor={theme.palette.divider}
              headerTextColor={theme.palette.text.primary}
              headerDateFormat="MMMM yyyy"
              barProgressColor={theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.45)' : 'rgba(255, 255, 255, 0.35)'}
              barProgressSelectedColor={theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.55)' : 'rgba(255, 255, 255, 0.45)'}
              barBackgroundColor="transparent"
              barBackgroundSelectedColor="transparent"
              projectProgressColor="rgba(255, 255, 255, 0.35)"
              projectProgressSelectedColor="rgba(255, 255, 255, 0.45)"
              projectBackgroundColor={theme.palette.mode === 'dark' ? '#66BB6A' : '#4CAF50'}
              projectBackgroundSelectedColor={theme.palette.mode === 'dark' ? '#81C784' : '#388E3C'}
              milestoneBackgroundColor={theme.palette.mode === 'dark' ? '#FF5983' : '#f50057'}
              milestoneBackgroundSelectedColor={theme.palette.mode === 'dark' ? '#FF80AB' : '#c51162'}
              arrowColor={theme.palette.primary.main}
              todayLineColor={theme.palette.mode === 'dark' ? theme.palette.primary.light : theme.palette.primary.main}
              todayLineWidth={2}
              todayLineLabel="Today"
              todayLineLabelColor={theme.palette.mode === 'dark' ? theme.palette.primary.light : theme.palette.primary.main}
              timeLineBackgroundColor={theme.palette.background.paper}
              timeLineColor={theme.palette.divider}
              calendarHeaderBackgroundColor={theme.palette.background.paper}
              calendarHeaderColor={theme.palette.text.primary}
              calendarBodyBackgroundColor={theme.palette.background.paper}
              calendarBodyColor={theme.palette.text.primary}
              calendarTodayColor={theme.palette.primary.main}
              gridLineColor={theme.palette.divider}
              gridLineWidth={1}
              selectedRowBackground={theme.palette.mode === 'dark' ? theme.palette.background.default : '#f5f5f5'}
              TooltipContent={TaskTooltip}
              TaskListHeader={TaskListHeader}
              TaskListTable={TaskListTable}
            />
            )}
          </Box>
        </Box>

        <TaskEditDialog
          open={editDialogOpen}
          task={selectedTask}
          projectTasks={projectTasks}
          productionPhases={productionPhases}
          onClose={() => {
            setEditDialogOpen(false);
            setSelectedTask(null);
          }}
          onSave={(updatedTask) => {
            if (selectedTask) {
              // Ensure we're using the original ID for backend communication
              const taskToUpdate = {
                ...updatedTask,
                // If the task has an originalId field, use that for the API call
                id: selectedTask.originalId || updatedTask.id
              };
              onUpdateTask(taskToUpdate);
            } else {
              onAddTask(updatedTask);
            }
            setEditDialogOpen(false);
            setSelectedTask(null);
          }}
          onDelete={selectedTask ? () => {
            // Use the original ID for deletion if available
            const idToDelete = selectedTask.originalId || selectedTask.id;
            onDeleteTask(idToDelete);
            setEditDialogOpen(false);
            setSelectedTask(null);
          } : undefined}
        />

        <TemplateDialog
          open={templateDialogOpen}
          onClose={() => setTemplateDialogOpen(false)}
          onApply={handleApplyTemplate}
        />
      </Paper>
    </DragDropContext>
  );
};

export default GanttChart;
